import React, { useState } from 'react';
import { ArrowUpRight, Mail, Phone, MapPin, CheckCircle2, Loader2 } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import SectionLabel from './SectionLabel';
import { CTA_URL, offices, teamMedia } from '../mock';
import { useI18n } from '../i18n/I18nContext';

const EMAILJS_SERVICE_ID = 'service_wv30dgm';
const EMAILJS_TEMPLATE_ID = 'template_3xcbm94';
const EMAILJS_PUBLIC_KEY = 'SZSgt-QSd38YNVEuA';

export default function Contact() {
  const { t } = useI18n();
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ from_name: '', from_email: '', phone: '', company: '', message: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.from_name,
          from_email: form.from_email,
          phone: form.phone,
          company: form.company || '—',
          message: form.message,
          reply_to: form.from_email,
          page_url: typeof window !== 'undefined' ? window.location.href : '',
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      setSent(true);
      toast({ title: t('contact.form.toast_ok_title'), description: t('contact.form.toast_ok_desc') });
      setForm({ from_name: '', from_email: '', phone: '', company: '', message: '' });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      console.error('EmailJS error:', err);
      toast({ title: t('contact.form.toast_err_title'), description: t('contact.form.toast_err_desc'), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kapcsolat" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-5">
              <SectionLabel>{t('contact.tag')}</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 leading-[1.05]">
                {t('contact.title_a')} <br />
                <span className="text-gradient-green">{t('contact.title_b')}</span>
              </h2>
            </div>

            <a href={CTA_URL} target="_blank" rel="noreferrer">
              <Button className="btn-primary bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl h-11 px-5">
                {t('common.free_consultation')}
                <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </Button>
            </a>

            <div className="space-y-6 pt-6">
              {(t('team.members') || []).map((m, i) => {
                const media = teamMedia[i] || {};
                return (
                  <div key={m.name} className="rounded-2xl border border-blue-500/10 bg-white p-5">
                    <div className="text-[11px] tracking-[0.2em] uppercase text-blue-600/70 mb-1">{m.role}</div>
                    <div className="font-display text-lg text-slate-900 mb-3">{m.name}</div>
                    <div className="space-y-1.5 text-sm text-slate-700/80">
                      <a href={`mailto:${media.email}`} className="flex items-center gap-2 hover:text-blue-600">
                        <Mail className="h-4 w-4 text-blue-600" /> {media.email}
                      </a>
                      <a href={`tel:${(media.phone || '').replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-blue-600">
                        <Phone className="h-4 w-4 text-blue-600" /> {media.phone}
                      </a>
                    </div>
                    <div className="mt-3 flex gap-1.5">
                      {(media.langs || '').split(' · ').map((l) => l && (
                        <span key={l} className="rounded-md border border-blue-500/20 px-1.5 py-0.5 text-[10px] tracking-widest text-blue-600">{l}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-2xl border border-blue-500/10 bg-white p-5">
              <div className="text-[11px] tracking-[0.2em] uppercase text-blue-600/70 mb-3">{t('contact.offices_label')}</div>
              <div className="grid grid-cols-2 gap-2 text-sm text-slate-700/75">
                {offices.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-blue-600" />
                    <span><span className="mr-1">{o.flag}</span>{o.address}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="relative rounded-3xl border border-blue-500/12 bg-gradient-to-br from-slate-50 to-white p-7 md:p-10 space-y-5 overflow-hidden">
              <div className="absolute -top-32 -right-32 h-64 w-64 rounded-full bg-blue-500/10 blur-[80px]" />
              <div className="relative space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField label={t('contact.form.name')}>
                    <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} required
                      className="h-12 bg-white border-blue-500/15 text-slate-900 placeholder:text-slate-700/30 focus:border-blue-500/60 focus-visible:ring-blue-500/20"
                      placeholder={t('contact.form.name_ph')} />
                  </FormField>
                  <FormField label={t('contact.form.email')}>
                    <Input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} required
                      className="h-12 bg-white border-blue-500/15 text-slate-900 placeholder:text-slate-700/30 focus:border-blue-500/60 focus-visible:ring-blue-500/20"
                      placeholder={t('contact.form.email_ph')} />
                  </FormField>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField label={t('contact.form.phone')}>
                    <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required
                      className="h-12 bg-white border-blue-500/15 text-slate-900 placeholder:text-slate-700/30 focus:border-blue-500/60 focus-visible:ring-blue-500/20"
                      placeholder={t('contact.form.phone_ph')} />
                  </FormField>
                  <FormField label={t('contact.form.company')} hint={t('contact.form.company_hint')}>
                    <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="h-12 bg-white border-blue-500/15 text-slate-900 placeholder:text-slate-700/30 focus:border-blue-500/60 focus-visible:ring-blue-500/20"
                      placeholder={t('contact.form.company_ph')} />
                  </FormField>
                </div>
                <FormField label={t('contact.form.message')}>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={5}
                    className="bg-white border-blue-500/15 text-slate-900 placeholder:text-slate-700/30 focus:border-blue-500/60 focus-visible:ring-blue-500/20 resize-none"
                    placeholder={t('contact.form.message_ph')} />
                </FormField>
                <Button type="submit" disabled={sending}
                  className="btn-primary w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl h-12 px-8 disabled:opacity-70">
                  {sending ? (<><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t('contact.form.sending')}</>)
                    : sent ? (<><CheckCircle2 className="mr-1.5 h-4 w-4" /> {t('contact.form.sent')}</>)
                    : (<>{t('contact.form.send')}<ArrowUpRight className="ml-1.5 h-4 w-4" /></>)}
                </Button>
                <p className="text-xs text-slate-700/40">{t('contact.form.privacy')}</p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <span className="text-[11px] tracking-[0.2em] uppercase text-blue-600/70">{label}</span>
        {hint && (
          <span className="text-[10px] tracking-normal normal-case text-slate-700/40 truncate">{hint}</span>
        )}
      </div>
      {children}
    </label>
  );
}
